import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { sellerLabel } from '~/utils';
import type { ItemRow } from '~/pages/rfq-view/itemGroups';
import { offeredBundleQuantity } from './quoteDisplay';
import type { QuoteOffering } from './quoteOrdering';
import { OfferedPrice } from './OfferedPrice';
import { isOfferSelectable } from './sellerOffer';
import { QuoteStatusLabel } from './QuoteStatusLabel';
import { quoteItemCheckboxId } from './splitAwardGridCells';
import { selectionLabelClass } from '~/components/ui/selectionCheckbox';
import type { ItemBlockData, Quote, QuoteItem } from './splitAwardGridTypes';

export function SellerRowsTable({
  row,
  offerings,
  pickupPriceLabel,
  deliveryPriceLabel,
  requestedBundles,
  lowestAmounts,
  selectedItemIds,
  onToggleItem,
  disabled,
}: Readonly<{
  row: ItemRow;
  offerings: ReadonlyArray<QuoteOffering<Quote, QuoteItem>>;
  pickupPriceLabel: string;
  deliveryPriceLabel: string;
  requestedBundles: number | null;
  lowestAmounts: ItemBlockData['lowestAmounts'];
  selectedItemIds: Set<string>;
  onToggleItem: (itemId: string) => void;
  disabled: boolean;
}>) {
  const { t } = useTranslation('rfq-view');

  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="text-left text-neut-sec">
          <th className="font-normal pb-1 pr-4 w-8" />
          <th className="font-normal pb-1 pr-4">
            {t('rfq_view.seller_label')}
          </th>
          <th className="font-normal pb-1 pr-4">
            {t('rfq_view.quantity_label')}
          </th>
          <th className="font-normal pb-1 pr-4">{pickupPriceLabel}</th>
          <th className="font-normal pb-1 pr-4">{deliveryPriceLabel}</th>
        </tr>
      </thead>
      <tbody>
        {offerings.map(({ quote, item }) => (
          <SellerRow
            key={quote.id}
            quote={quote}
            item={item}
            row={row}
            requestedBundles={requestedBundles}
            lowestAmounts={lowestAmounts}
            isSelected={selectedItemIds.has(item.id)}
            onToggle={onToggleItem}
            disabled={disabled}
          />
        ))}
      </tbody>
    </table>
  );
}

function SellerRow({
  quote,
  item,
  row,
  requestedBundles,
  lowestAmounts,
  isSelected,
  onToggle,
  disabled,
}: Readonly<{
  quote: Quote;
  item: QuoteItem;
  row: ItemRow;
  requestedBundles: number | null;
  lowestAmounts: ItemBlockData['lowestAmounts'];
  isSelected: boolean;
  onToggle: (itemId: string) => void;
  disabled: boolean;
}>) {
  const { t } = useTranslation('rfq-view');
  const order = item.order;
  const isSelectable = isOfferSelectable(quote, item, disabled);
  const bundleQuantity = offeredBundleQuantity(item, row);
  const isShort = requestedBundles != null && bundleQuantity < requestedBundles;
  const sellerName = sellerLabel(quote.sellingOrganization);

  return (
    <tr data-testid={`seller-row-${quote.id}-${row.platformProductLengthId}`}>
      <td className="pr-4 py-1">
        <input
          id={quoteItemCheckboxId(item.id)}
          type="checkbox"
          checked={isSelected}
          disabled={!isSelectable}
          onChange={() => onToggle(item.id)}
          aria-label={t('rfq_view.select_seller_item_aria_label', {
            sellerName,
          })}
          data-testid={`quote-item-checkbox-${item.id}`}
        />
      </td>
      <td className="pr-4 py-1 text-neut-prim">
        <label
          htmlFor={quoteItemCheckboxId(item.id)}
          className={selectionLabelClass(!isSelectable)}
        >
          {sellerName}
        </label>
        <QuoteStatusLabel
          quote={quote}
          className="ml-2 text-xs font-normal text-neut-sec"
        />
        {order && (
          <Link
            to={`/orders/view/${order.id}`}
            className="ml-2 text-xs text-prim underline"
            data-testid={`ordered-link-${item.id}`}
          >
            {t('rfq_view.quote_item_ordered_label')}
          </Link>
        )}
      </td>
      <td className="pr-4 py-1 text-neut-prim">
        {bundleQuantity}
        {isShort && (
          <span className="ml-1 text-xs text-warning-700">
            {t('rfq_view.quantity_short_label', {
              requested: requestedBundles,
            })}
          </span>
        )}
      </td>
      <td className="pr-4 py-1 text-neut-prim">
        <OfferedPrice
          itemId={item.id}
          price={item.offeredPickupPrice}
          lowestAmount={lowestAmounts.pickup}
          kind="pickup"
        />
      </td>
      <td className="pr-4 py-1 text-neut-prim">
        <OfferedPrice
          itemId={item.id}
          price={item.offeredDeliveryPrice}
          lowestAmount={lowestAmounts.delivery}
          kind="delivery"
        />
      </td>
    </tr>
  );
}
